import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Clock, MapPin, Ticket, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { todayAST } from "@/lib/date";
import PatientNav from "@/components/PatientNav";
import StatusBadge from "@/components/StatusBadge";
import LiveTurn from "@/components/LiveTurn";
import RescheduleDialog from "@/components/RescheduleDialog";

const ACTIVE = ["pending", "confirmed", "arrived", "in_progress"];
const today = todayAST;

export default function MyAppointments() {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);

  const load = async () => {
    const { data } = await api.get("/appointments/mine");
    setAppts(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.patch(`/appointments/${id}/cancel`);
      toast.success("Cita cancelada");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const upcoming = appts.filter((a) => ACTIVE.includes(a.status));
  const history = appts.filter((a) => !ACTIVE.includes(a.status));
  const liveOnes = upcoming.filter((a) => a.date === today() && a.modality === "CUPOS");

  return (
    <div className="min-h-screen bg-slate-50">
      <PatientNav />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Mis citas</h1>

        {liveOnes.map((a) => (
          <div key={`live-${a.id}`} className="mt-6">
            <LiveTurn appointment={a} />
          </div>
        ))}

        <section className="mt-8">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400">Próximas</h2>
          <div className="mt-3 space-y-3">
            {loading && <p className="text-sm font-semibold text-slate-400">Cargando…</p>}
            {!loading && upcoming.length === 0 && (
              <div className="rounded-2xl border border-dashed border-[#E2E8F0] bg-white p-10 text-center">
                <CalendarClock className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 text-sm font-bold text-slate-500">No tienes citas próximas.</p>
                <Link to="/buscar"><Button className="mt-4 rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="empty-search-btn">Buscar médicos</Button></Link>
              </div>
            )}
            {upcoming.map((a) => (
              <ApptCard key={a.id} a={a} onCancel={cancel} onReschedule={setRescheduleAppt} />
            ))}
          </div>
        </section>

        {history.length > 0 && (
          <section className="mt-10">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400">Historial</h2>
            <div className="mt-3 space-y-3">
              {history.map((a) => <ApptCard key={a.id} a={a} history />)}
            </div>
          </section>
        )}
      </div>

      {rescheduleAppt && (
        <RescheduleDialog
          appointment={rescheduleAppt}
          open={!!rescheduleAppt}
          onOpenChange={(v) => { if (!v) setRescheduleAppt(null); }}
          onDone={() => { setRescheduleAppt(null); load(); }}
        />
      )}
    </div>
  );
}

function ApptCard({ a, onCancel, onReschedule, history }) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid={`appt-${a.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-extrabold tracking-tight text-slate-900">{a.doctor_name}</h3>
          <p className="text-sm font-bold text-[#0284C7]">{a.specialty}</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold text-slate-500">
            <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-slate-400" /> {a.date}</span>
            {a.modality === "HORAS" ? (
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" /> {a.slot_time}</span>
            ) : (
              <span className="flex items-center gap-1.5"><Ticket className="h-4 w-4 text-slate-400" /> {a.turn_number ? `Turno ${a.turn_number}` : "Por llegada"}</span>
            )}
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" /> {a.center}</span>
          </div>
        </div>
        <StatusBadge status={a.status} testid={`appt-status-${a.id}`} />
      </div>
      {!history && onCancel && (
        <div className="mt-4 flex justify-end gap-2 border-t border-[#E2E8F0] pt-4">
          {(a.status === "pending" || a.status === "confirmed") && onReschedule && (
            <Button variant="outline" onClick={() => onReschedule(a)} className="rounded-full border-[#E2E8F0] font-bold text-[#0284C7]" data-testid={`reschedule-appt-${a.id}`}>
              <RefreshCw className="mr-1.5 h-4 w-4" /> Reprogramar
            </Button>
          )}
          <Button variant="ghost" onClick={() => onCancel(a.id)} className="rounded-full font-bold text-red-500 hover:bg-red-50" data-testid={`cancel-appt-${a.id}`}>
            <XCircle className="mr-1.5 h-4 w-4" /> Cancelar cita
          </Button>
        </div>
      )}
    </div>
  );
}
